import React, { useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import XLRLogger from 'src/common/helpers/logger';
import { ITouchpointTheme } from 'src/common/interfaces/dtos/ITouchpointTheme';
import { AppState, store } from 'src/common/redux/configure-store';
import { IDeviceConfigurationDictionary } from 'src/device-status/interfaces/IDeviceConfigurationDictionary';
import { ThemeContext } from 'src/emerald-theme-provider';
import useCustomerDisplay from './hooks/useCustomerDisplay';

const CustomerDisplayProxy: React.FC<CustomerDisplayStateProps> = ({
  customerDisplay,
  itemState,
  scale,
  deviceStatus,
  tenderingInitialState,
  POSModeState,
  showDepositItems,
  transactionState,
  tenderExchange,
}: CustomerDisplayStateProps): null => {
  const showDisplay = deviceStatus.hasCustomerDisplay;
  const deviceList: IDeviceConfigurationDictionary[] | undefined = store.getState().touchPoint
    .touchPointConfiguration.deviceList;
  const customerDisplayConfig: IDeviceConfigurationDictionary | undefined = deviceList?.find(
    d => d.LogicalName === 'CustomerDisplay',
  );

  let customerDisplayNumber: number | undefined;
  try {
    customerDisplayNumber = customerDisplayConfig?.DisplayNumber
      ? parseInt(customerDisplayConfig.DisplayNumber)
      : undefined;
  } catch (e) {
    console.log(e);
  }
  // create customer display
  const [messagePort, setDisplayState] = useCustomerDisplay<CustomerDisplayProxyProps>(
    showDisplay,
    customerDisplayNumber,
  );

  const { emeraldTheme } = useContext(ThemeContext);

  // hooking up logging from the customer display
  // no data needed back from it at this time
  useEffect(() => {
    messagePort.onmessage = event =>
      XLRLogger.debug({
        message: 'Message from customer display:',
        data: event.data,
        source: 'customer-display-proxy',
      });
  }, [messagePort]);

  useEffect(() => {
    setDisplayState({ customerDisplay });
  }, [customerDisplay, setDisplayState]);

  // for passing state data into the customer display

  useEffect(() => {
    setDisplayState({ itemState });
  }, [setDisplayState, itemState]);

  useEffect(() => {
    setDisplayState({ scale });
  }, [scale, setDisplayState]);

  useEffect(() => {
    setDisplayState({ deviceStatus });
  }, [deviceStatus, setDisplayState]);

  useEffect(() => {
    setDisplayState({ tenderingInitialState });
  }, [tenderingInitialState, setDisplayState]);

  useEffect(() => {
    setDisplayState({ emeraldTheme });
  }, [emeraldTheme, setDisplayState]);

  useEffect(() => {
    setDisplayState({ POSModeState });
  }, [POSModeState, setDisplayState]);

  useEffect(() => {
    setDisplayState({ showDepositItems });
  }, [showDepositItems, setDisplayState]);

  useEffect(() => {
    setDisplayState({ transactionState });
  }, [transactionState, setDisplayState]);

  useEffect(() => {
    setDisplayState({ tenderExchange });
  }, [tenderExchange, setDisplayState]);

  return null;
};

const mapStateToProps = (state: AppState) => ({
  customerDisplay: state.customerDisplayScreen.currentScreen,
  itemState: state.itemList,
  scale: state.scale,
  deviceStatus: state.deviceStatus,
  tenderingInitialState: state.tendering,
  POSModeState: state.posMode,
  showDepositItems:
    state.touchPoint.touchPointConfiguration.depositConfigurations
      ?.displayDepositItemOnCashierDisplayAndCustomerDisplay,
  transactionState: state.transaction,
  tenderExchange: state.tenderExchange,
});

interface CustomerDisplayThemeProps {
  emeraldTheme: ITouchpointTheme;
}

export default connect(mapStateToProps)(CustomerDisplayProxy);

type CustomerDisplayStateProps = ReturnType<typeof mapStateToProps>;

export type CustomerDisplayProxyProps = Partial<
  CustomerDisplayStateProps & CustomerDisplayThemeProps
>;
